// Real camera QR scanner: getUserMedia (rear camera on phones) + jsQR decoding of video frames.
import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { parseChipPayload } from "./chip";

type CamState = "starting" | "live" | "denied" | "unavailable" | "error";

export function QrScanner({ onChip }: { onChip: (chip: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<CamState>("starting");
  const [hint, setHint] = useState<string | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string | undefined>();
  const done = useRef(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let last = 0;
    let cancelled = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) return setState("unavailable");
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        if (cancelled) return stream.getTracks().forEach((t) => t.stop());
        const v = video.current!;
        v.srcObject = stream;
        await v.play();
        setState("live");
        navigator.mediaDevices.enumerateDevices().then((d) => setDevices(d.filter((x) => x.kind === "videoinput"))).catch(() => {});
        raf = requestAnimationFrame(tick);
      } catch (e) {
        const name = (e as DOMException).name;
        setState(name === "NotAllowedError" || name === "SecurityError" ? "denied" : name === "NotFoundError" || name === "OverconstrainedError" ? "unavailable" : "error");
      }
    }

    function tick(t: number) {
      raf = requestAnimationFrame(tick);
      const v = video.current;
      if (!v || !ctx || done.current || v.readyState < 2 || t - last < 120) return;
      last = t;
      // Decode a downscaled centre square — faster and matches the viewfinder.
      const side = Math.min(v.videoWidth, v.videoHeight);
      const size = Math.min(side, 560);
      canvas.width = canvas.height = size;
      ctx.drawImage(v, (v.videoWidth - side) / 2, (v.videoHeight - side) / 2, side, side, 0, 0, size, size);
      const code = jsQR(ctx.getImageData(0, 0, size, size).data, size, size, { inversionAttempts: "attemptBoth" });
      if (!code?.data) return;
      const chip = parseChipPayload(code.data);
      if (chip) {
        done.current = true;
        onChip(chip);
      } else {
        setHint("This QR code is not a Vetproof pet ID");
      }
    }

    start();
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [deviceId, onChip]);

  useEffect(() => {
    if (!hint) return;
    const t = setTimeout(() => setHint(null), 2200);
    return () => clearTimeout(t);
  }, [hint]);

  return (
    <div className="cam">
      <video ref={video} playsInline muted autoPlay className={state === "live" ? "" : "hidden"} />
      {state === "live" && (
        <>
          <div className="viewfinder"><i /><i /><i /><i /><div className="laser" /></div>
          <div className="cam-caption">Point the camera at the owner's Vetproof QR code</div>
          {devices.length > 1 && (
            <button className="cam-switch" onClick={() => {
              const i = devices.findIndex((d) => d.deviceId === deviceId);
              setDeviceId(devices[(i + 1) % devices.length].deviceId);
              setState("starting");
            }} aria-label="Switch camera">⟲</button>
          )}
        </>
      )}
      {state === "starting" && <div className="cam-msg"><div className="spinner" />Starting camera…</div>}
      {state === "denied" && <div className="cam-msg">📷<b>Camera access was blocked</b>Allow camera access for this site in the browser settings, or use the RFID reader / manual entry.</div>}
      {state === "unavailable" && <div className="cam-msg">📷<b>No camera available</b>This device has no camera the browser can use.</div>}
      {state === "error" && <div className="cam-msg">📷<b>Camera could not start</b>Close other apps using the camera and try again.</div>}
      {hint && <div className="cam-hint">{hint}</div>}
    </div>
  );
}
